import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { KioskMe, KioskProfile, KioskProfileBody } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { kioskApi } from "../../api/client.js";

/** A typed "you are not a SuperAdmin" — the page shows the no-access line instead of an error (mirrors sensors). */
export class KioskForbiddenError extends Error {
  readonly code = "KIOSK_FORBIDDEN" as const;
  constructor() {
    super("GET /kiosk/profiles returned 403");
  }
}
/** The repo's `fail()` shape, so the shared error surface resolves the mapped copy (KIOSK_PROFILE_STALE, …). */
interface KioskWriteError extends Error {
  status: number;
  code?: string;
  body: unknown;
}
function fail(status: number, body: unknown): KioskWriteError {
  const err = new Error(`kiosk write failed (${String(status)})`) as KioskWriteError;
  err.status = status;
  err.code = (body as { code?: string } | undefined)?.code;
  err.body = body;
  return err;
}

const profilesKey = (session: AuthSession | null) => ["kiosk-profiles", session?.realm, session?.sub];

/** `GET /kiosk/profiles` (ADR-036) — SuperAdmin-only, so a 403 is the VERDICT, not a failure to retry. */
export function useKioskProfiles(session: AuthSession | null) {
  return useQuery({
    queryKey: profilesKey(session),
    enabled: session !== null,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await kioskApi.listProfiles();
      if (res.status === 403) throw new KioskForbiddenError();
      if (res.status !== 200) throw new Error(`GET /kiosk/profiles returned ${String(res.status)}`);
      return res.body.profiles;
    },
    retry: (count, error) => !(error instanceof KioskForbiddenError) && count < 1,
  });
}

export function useCreateKioskProfile(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation<KioskProfile, Error, KioskProfileBody>({
    mutationFn: async (body) => {
      const res = await kioskApi.createProfile({ body });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: profilesKey(session) }),
  });
}

export function useUpdateKioskProfile(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation<KioskProfile, Error, { id: number; body: KioskProfileBody & { expectedUpdatedAt: string } }>({
    mutationFn: async ({ id, body }) => {
      const res = await kioskApi.updateProfile({ params: { id }, body });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    /**
     * `onSettled`, not `onSuccess` (diff panel, Medium): a 409 KIOSK_PROFILE_STALE tells the admin to reopen the
     * profile and redo the change, but the list is cached for a minute — without this refetch, reopening hands back
     * the SAME obsolete token and every retry 409s again for up to 60s. Refreshing on failure too is what makes the
     * advertised recovery actually work.
     */
    onSettled: () => void qc.invalidateQueries({ queryKey: profilesKey(session) }),
  });
}

export function useDeleteKioskProfile(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, { id: number }>({
    mutationFn: async ({ id }) => {
      const res = await kioskApi.deleteProfile({ params: { id }, body: {} });
      if (res.status !== 200) throw fail(res.status, res.body);
      return res.body;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: profilesKey(session) }),
  });
}

/**
 * `GET /kiosk/me` — the kiosk's own read (ADR-036). It never errors into a chrome-less screen: an unknown profile
 * comes back as `requestedProfileMissing` with a picker, which is what the landing screen renders.
 */
export function useMyKiosk(session: AuthSession | null, profileId: number | null) {
  return useQuery<KioskMe, Error>({
    queryKey: ["kiosk-me", session?.realm, session?.sub, profileId],
    enabled: session !== null,
    staleTime: 60_000,
    // The id is part of the key, and the device changes it on its own — it persists the id it just resolved, and
    // re-points itself when a profile is deleted. Without this, each of those would drop the screen back to a
    // full-page "Loading…" on a wall-mounted display that was already showing the right thing.
    placeholderData: keepPreviousData,
    /**
     * A KIOSK IS NEVER RELOADED (diff panel, Medium). The app disables `refetchOnWindowFocus` globally
     * (`main.tsx`), and a wall-mounted screen has no focus events, no navigation and nobody to press F5 — so without
     * a poll the device would never observe its profile being deleted or re-scoped, and every self-recovery path in
     * this feature would be unreachable in production. Five minutes: far below the "someone notices the wrong screen"
     * threshold, far above anything that matters for one small row per device.
     */
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: true,
    queryFn: async () => {
      const res = await kioskApi.getMyKiosk({ query: profileId === null ? {} : { profileId } });
      if (res.status !== 200) throw new Error(`GET /kiosk/me returned ${String(res.status)}`);
      return res.body;
    },
  });
}
